/**
 * WorkdayClock — arbetsdag-stämpling överst på Stämpelklockan.
 *
 * Regler som implementeras här:
 *  - En användare kan bara ha EN aktiv arbetsdag åt gången (backend gate).
 *  - Utan aktiv arbetsdag är objekt-stämpling nedanför blockerad (backend gate).
 *  - Vid utstämpling räknas rast-avdrag och aktiva objekt-stämplingar
 *    auto-stängs (regel C) — det sköts av backend.
 *
 * Emittar `onChange` när workday-state ändras så förälder-Stämpelklockan
 * kan reload:a både workday och objekt-active-state.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Play,
  Stop,
  MapPin,
  Clock,
  Coffee,
  CheckCircle,
} from "@phosphor-icons/react";

function fmtHHMM(minutes) {
  if (minutes == null || isNaN(minutes)) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return `${h}h ${m.toString().padStart(2, "0")}min`;
}

function fmtLiveDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

export default function WorkdayClock({ onChange }) {
  const [current, setCurrent] = useState(null); // null = ingen aktiv dag
  const [workplaces, setWorkplaces] = useState([]);
  const [selectedWp, setSelectedWp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [summary, setSummary] = useState(null); // efter utstämpling

  const load = async () => {
    try {
      const [c, wps] = await Promise.all([
        api.get("/workday/current"),
        api.get("/workday/workplaces"),
      ]);
      const wpList = (wps.data || []).filter((w) => w.active !== false);
      setWorkplaces(wpList);
      setCurrent(c.data || null);
      if (!c.data && wpList.length > 0 && !selectedWp) {
        setSelectedWp(wpList[0].workplace_id);
      }
    } catch (e) {
      console.error("WorkdayClock load", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Live-timer när dagen är aktiv
  useEffect(() => {
    if (!current) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [current]);

  const liveSeconds = useMemo(() => {
    if (!current?.stamp_in) return 0;
    const start = new Date(current.stamp_in).getTime();
    return (now - start) / 1000;
  }, [current, now]);

  const stampIn = async () => {
    try {
      setSubmitting(true);
      const res = await api.post("/workday/in", {
        workplace_id: selectedWp || null,
      });
      setCurrent(res.data);
      toast.success("Arbetsdag påbörjad");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte stämpla in arbetsdag");
    } finally {
      setSubmitting(false);
    }
  };

  const stampOut = async () => {
    try {
      setSubmitting(true);
      const res = await api.post("/workday/out", {});
      setCurrent(null);
      setSummary(res.data);
      toast.success("Arbetsdag klar");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte stämpla ut");
    } finally {
      setSubmitting(false);
    }
  };

  // Sammanfattning efter utstämpling (visas tills stängd)
  if (summary) {
    return (
      <WorkdaySummaryCard summary={summary} onClose={() => setSummary(null)} />
    );
  }

  // AKTIV ARBETSDAG
  if (current) {
    return (
      <div
        className="rounded-2xl p-6 shadow-lg text-white"
        style={{ background: "linear-gradient(135deg, #1a2e25 0%, #22392e 100%)" }}
        data-testid="workday-active-card"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              Arbetsdag pågår
            </div>
            <div
              className="font-heading text-3xl md:text-4xl mt-2 tabular-nums"
              data-testid="workday-live-duration"
            >
              {fmtLiveDuration(liveSeconds)}
            </div>
            <div className="text-sm text-white/70 mt-1 flex items-center gap-1.5">
              <MapPin size={14} weight="fill" />
              {current.workplace_name || "Ingen arbetsplats"} · Instämplad{" "}
              {new Date(current.stamp_in).toLocaleTimeString("sv-SE", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
          <button
            onClick={stampOut}
            disabled={submitting}
            className="bg-white text-[#1a2e25] hover:bg-white/90 disabled:opacity-60 px-6 py-3 rounded-md font-medium flex items-center gap-2 shrink-0"
            data-testid="workday-stamp-out-btn"
          >
            <Stop size={18} weight="fill" />
            {submitting ? "Stänger..." : "Stämpla ut arbetsdag"}
          </button>
        </div>
      </div>
    );
  }

  // INTE INSTÄMPLAD — visa uppmaning + arbetsplats-dropdown
  return (
    <div
      className="bg-white border-2 border-brand rounded-2xl p-6 shadow-md"
      data-testid="workday-stamp-in-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs text-text-muted uppercase tracking-widest">
            Din arbetsdag
          </div>
          <div className="font-heading text-xl mt-1">Inte instämplad</div>
        </div>
        <div className="w-11 h-11 rounded-full bg-brand/10 flex items-center justify-center">
          <Clock size={22} weight="duotone" className="text-brand" />
        </div>
      </div>

      {workplaces.length === 0 ? (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm">
            Inga arbetsplatser är registrerade än. Du kan stämpla in utan
            arbetsplats — admin kan lägga till dem senare under{" "}
            <strong>Administrera → Löneinställningar</strong>.
          </div>
          <button
            onClick={stampIn}
            disabled={submitting}
            className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white px-6 py-3 rounded-md font-medium flex items-center justify-center gap-2"
            data-testid="workday-stamp-in-btn"
          >
            <Play size={18} weight="fill" />
            {submitting ? "Stämplar..." : "Stämpla in arbetsdag"}
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-muted">
              Arbetsplats
            </label>
            <select
              value={selectedWp}
              onChange={(e) => setSelectedWp(e.target.value)}
              className="w-full rounded-md border border-app bg-app px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand/40"
              data-testid="workday-workplace-select"
            >
              {workplaces.map((w) => (
                <option key={w.workplace_id} value={w.workplace_id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={stampIn}
            disabled={submitting || !selectedWp}
            className="bg-brand hover:bg-brand-hover disabled:opacity-60 text-white px-6 py-3 rounded-md font-medium flex items-center gap-2 shrink-0"
            data-testid="workday-stamp-in-btn"
          >
            <Play size={18} weight="fill" />
            {submitting ? "Stämplar..." : "Stämpla in arbetsdag"}
          </button>
        </div>
      )}

      <p className="mt-3 text-xs text-text-muted">
        💡 Objekt-stämpling nedan öppnas när du stämplat in på din arbetsdag.
      </p>
    </div>
  );
}

function WorkdaySummaryCard({ summary, onClose }) {
  const [markingDone, setMarkingDone] = useState(false);
  const [markedDoneAt, setMarkedDoneAt] = useState(
    summary.employee_marked_done_at || null
  );

  const markDone = async () => {
    setMarkingDone(true);
    try {
      const r = await api.post(`/workday/${summary.workday_id}/mark-done`);
      setMarkedDoneAt(r.data.employee_marked_done_at || new Date().toISOString());
      toast.success(
        "Arbetsdagen är markerad som klar. Väntar nu på attest från din chef."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte markera som klar");
    } finally {
      setMarkingDone(false);
    }
  };

  const inTime = new Date(summary.stamp_in).toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const outTime = new Date(summary.stamp_out).toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateStr = new Date(summary.stamp_in).toLocaleDateString("sv-SE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return (
    <div
      className="bg-white border border-app rounded-2xl overflow-hidden shadow-md"
      data-testid="workday-summary-card"
    >
      <div className="bg-brand text-white px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CheckCircle size={22} weight="fill" />
          <div>
            <div className="font-heading text-base">Arbetsdag klar</div>
            <div className="text-xs text-white/80 capitalize">
              {dateStr} · {summary.workplace_name || "Ingen arbetsplats"}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/80 hover:text-white text-sm"
          data-testid="workday-summary-close"
        >
          Stäng
        </button>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-app rounded-md p-3 text-center">
            <div className="text-[10px] text-text-muted uppercase tracking-wider">
              In
            </div>
            <div className="font-heading text-xl mt-1 tabular-nums">
              {inTime}
            </div>
          </div>
          <div className="bg-app rounded-md p-3 text-center">
            <div className="text-[10px] text-text-muted uppercase tracking-wider">
              Ut
            </div>
            <div className="font-heading text-xl mt-1 tabular-nums">
              {outTime}
            </div>
          </div>
          <div className="bg-brand/10 border border-brand/30 rounded-md p-3 text-center">
            <div className="text-[10px] text-brand uppercase tracking-wider font-medium">
              Brutto
            </div>
            <div className="font-heading text-xl mt-1 text-brand">
              {fmtHHMM(summary.gross_minutes)}
            </div>
          </div>
        </div>

        {(summary.deducted_breaks || []).length > 0 && (
          <>
            <div className="text-[10px] font-medium text-text-muted uppercase tracking-wider mb-2">
              Raster som drogs av
            </div>
            <div className="space-y-1.5 mb-4">
              {summary.deducted_breaks.map((b) => (
                <div
                  key={b.break_id}
                  className="flex items-center justify-between bg-app rounded-md px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <Coffee size={14} className="text-brand" weight="duotone" />
                    <span className="font-medium">{b.name}</span>
                    <span className="text-xs text-text-muted">
                      {b.start_time}–{b.end_time}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-brand">
                    −{b.duration_minutes} min
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="border-t border-app pt-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] text-text-muted uppercase tracking-wider">
              Lönegrundande netto
            </div>
            <div
              className="font-heading text-2xl"
              style={{ color: "var(--brand-forest, #1a2e25)" }}
            >
              {fmtHHMM(summary.net_minutes)}
            </div>
          </div>
          <div className="text-right text-xs text-text-muted">
            <div>Brutto {fmtHHMM(summary.gross_minutes)}</div>
            <div>Avdrag {summary.deducted_minutes || 0} min</div>
          </div>
        </div>

        {/* Fas 3 — anställd markerar dagen som klar för attest */}
        <div className="mt-4 pt-3 border-t border-app">
          {markedDoneAt ? (
            <div
              className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-sm flex items-center gap-2 text-blue-900"
              data-testid="workday-marked-done"
            >
              <CheckCircle size={16} weight="fill" className="text-blue-700" />
              <span>
                Klar för attest sedan{" "}
                <strong>
                  {new Date(markedDoneAt).toLocaleTimeString("sv-SE", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </strong>
                . Din chef ser den på Attest-sidan.
              </span>
            </div>
          ) : (
            <button
              onClick={markDone}
              disabled={markingDone}
              className="w-full border-2 border-brand text-brand hover:bg-brand hover:text-white disabled:opacity-60 transition px-4 py-2.5 rounded-md font-medium flex items-center justify-center gap-2"
              data-testid="workday-mark-done-btn"
            >
              <CheckCircle size={16} weight="fill" />
              {markingDone
                ? "Markerar..."
                : "Markera dagen som klar för attest"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
